import Breadcrumb from "@shared/components/ui-kit/navigation/Breadcrumb/Breadcrumb.web";
import BreadcrumbItem from "@shared/components/ui-kit/navigation/BreadcrumbItem/BreadcrumbItem.web";
import BreadcrumbLink from "@shared/components/ui-kit/navigation/BreadcrumbLink/BreadcrumbLink.web";
import { JSONLDBreadcrumbListItemWithServerProps } from "@shared/utils/seo/breadcrumbs";
import { useTranslation } from "@shared/utils/internationalization";
import { Namespace } from "i18next";
import Text from "@ui-kit/typography/Text/Text.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";

type BreadcrumbsProps = {
  crumbs: Array<JSONLDBreadcrumbListItemWithServerProps>;
  ns?: Namespace; // The translation namespace to apply on all breadcrumbs, if available
};

/**
 * Renders breadcrumbs.
 * Requires an array that follows the JSON-LD BreadcrumbList schema.
 * Per the schema, the last item in the array is the current page, or position N where N is the length of the array.
 */
const Breadcrumbs = ({ crumbs, ns = "discovery" }: BreadcrumbsProps) => {
  const { t } = useTranslation(ns);

  return (
    <Breadcrumb
      separator={
        <Icon.ChevronLeft
          boxSize="element.xs"
          marginBottom="none"
          color="gray.400"
        />
      }
      spacing={[crumbs.length > 2 ? "4px" : "8px", null, "16px"]}
    >
      {crumbs.map((crumb, index) => {
        // Assume that the last item in the crumbs array is the current page.
        const isCurrentPage = index === crumbs.length - 1;

        return (
          <BreadcrumbItem key={crumb.item} isCurrentPage={isCurrentPage}>
            <BreadcrumbLink
              href={crumb.item}
              isCurrentPage={isCurrentPage}
              fontSize="0.875rem"
              fontWeight={isCurrentPage ? "bold" : "normal"}
              color={isCurrentPage ? "font.dark" : "gray.600"}
              cursor={isCurrentPage ? "default" : "pointer"}
              _hover={{
                textDecoration: "none",
              }}
            >
              <Text color="inherit" noOfLines={1} wordBreak="break-all">
                {t(crumb.name, { ns, ...crumb.interpolation })}
              </Text>
            </BreadcrumbLink>
          </BreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );
};

export default Breadcrumbs;
